/**
 * Sperren/Entsperren einer Person (D3, Spec 25) — Knopf plus Rückfrage, eine
 * Bauform für beide Stellen aus der Designabnahme: `AccessPersonSheet`
 * (unter 768px, im Bearbeiten-Sheet) und `routes/AccessUser.tsx::
 * PersonHeadBlock` (ab 768px, neben „Bearbeiten"). Mutationen,
 * Rückfrage-Zustand, Fehlertext, Bezeichnung und Test-IDs standen zuvor an
 * beiden Stellen wortgleich doppelt (Tor-B-Simplifier-Befund).
 *
 * `ConfirmationStrip` ist selbst `fixed` positioniert
 * (`components/ConfirmationStrip.tsx`) — ihr Platz im DOM-Baum ändert nichts
 * am Aussehen; sie muss nur innerhalb von `SheetContent` bleiben, sonst
 * markiert Radix sie beim offenen Dialog `aria-hidden`. Beide Aufrufer
 * behalten deshalb ihre eigene Platzierung des Knopfs (D3) und binden diese
 * Komponente genau dort ein.
 */

import { useState } from 'react'

import { ConfirmationStrip } from '@/components/ConfirmationStrip'
import { ApiError } from '@/lib/api'
import { useDisableUser, useEnableUser } from '@/features/access/api'

interface PersonDisableActionProps {
  readonly personId: string
  readonly isDisabled: boolean
  /** Sheet-Variante (unter 768px): `--tap-min` nötig. Am Schreibtisch reicht
   * der Zeiger (36px, `design-system`) — deshalb hier weglassen. */
  readonly tapMin?: boolean
  readonly onError: (message: string) => void
}

export function PersonDisableAction({ personId, isDisabled, tapMin = false, onError }: PersonDisableActionProps) {
  const disableMutation = useDisableUser()
  const enableMutation = useEnableUser()
  const [isPendingConfirm, setIsPendingConfirm] = useState(false)

  async function confirmDisable() {
    setIsPendingConfirm(false)
    try {
      await disableMutation.mutateAsync(personId)
    } catch (error) {
      onError(error instanceof ApiError ? error.message : 'Die Verbindung zum Server ist unterbrochen.')
    }
  }

  async function enable() {
    try {
      await enableMutation.mutateAsync(personId)
    } catch (error) {
      onError(error instanceof ApiError ? error.message : 'Die Verbindung zum Server ist unterbrochen.')
    }
  }

  return (
    <>
      <button
        type="button"
        data-testid={isDisabled ? 'access-person-enable' : 'access-person-disable'}
        onClick={() => {
          if (isDisabled) void enable()
          else setIsPendingConfirm(true)
        }}
        className={`rounded-card border-border border px-4 text-body transition-colors duration-(--duration-fast) ease-(--ease-out) ${
          tapMin ? 'min-h-(--tap-min) ' : ''
        }${isDisabled ? 'text-text hover:bg-bg' : 'text-status-overdue'}`}
      >
        {isDisabled ? 'Entsperren' : 'Sperren'}
      </button>
      <ConfirmationStrip
        testId="access-person-disable-confirmation"
        message={isPendingConfirm ? 'Person sperren? Sie sieht dann nichts mehr und kann sich nicht anmelden.' : null}
        onDismiss={() => {
          setIsPendingConfirm(false)
        }}
        confirm={{
          confirmLabel: 'Sperren',
          confirmTestId: 'access-person-disable-confirm',
          cancelLabel: 'Abbrechen',
          cancelTestId: 'access-person-disable-cancel',
          onConfirm: () => void confirmDisable(),
        }}
      />
    </>
  )
}
